import type { CSS } from '@stitches/react';
import dynamic from 'next/dynamic';
import React, { memo, useCallback, useMemo, useState } from 'react';

import { IconUero5Max } from '@/components/layouts/header/icon-uero-5-max';
import { Badge } from '@/components/layouts/header/submenu-model-link';
import { CarModel } from '@/components/sections/product-compare/car-selection-card';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Image } from '@/components/shared/media/image';
import {
  IMAGE_VIEW,
  ModelImageProps,
  ModelSlider,
} from '@/components/shared/model-slider';
import { PaginationBar } from '@/components/shared/navigation/pagination-bar';
import { DEFAULT_IMAGE } from '@/contents/lcv/lcv-compare';
import { styled } from '@/stitches.config';

const Modal = dynamic(() => import('@/components/shared/overlays/modal'), {
  ssr: false,
});

const SlideContainerStyled = styled(Flex, {
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  variants: {
    disabled: {
      true: {
        '& .swiper-wrapper': {
          transform: 'translate3d(0px, 0px, 0px) !important',
        },
      },
    },
  },
});

interface CarSelectionSliderProps {
  css?: CSS;
  name?: string;
  model: CarModel;
  activeSliderIndex: number;
  currentIndex: number;
  ratio?: '4:3' | '16:9';
  showBadge?: boolean;
  onChange: (swiper: any, image: ModelImageProps) => void;
  onClick?: (
    swiper: any,
    event: MouseEvent | TouchEvent | PointerEvent
  ) => void;
}

interface ModalCompareState {
  open: boolean;
  data: { index: number } | null;
}

export const CarSelectionSlider: React.FC<CarSelectionSliderProps> = memo(
  ({
    css,
    model,
    onChange,
    onClick,
    activeSliderIndex,
    currentIndex,
    name = 'car-selection',
    ratio = '16:9',
    showBadge = false,
  }) => {
    const [modalCompare, setModalCompare] = useState<ModalCompareState>({
      open: false,
      data: null,
    });

    const onOpenModal = useCallback(
      (index: number) => {
        if (!model?.price) return;
        setModalCompare({ open: true, data: { index } });
      },
      [model?.price]
    );

    const onCloseModal = useCallback(() => {
      setModalCompare({ open: false, data: null });
    }, []);

    const images = useMemo(() => {
      const list = [
        {
          label: 'ด้านหน้า',
          view: IMAGE_VIEW.FRONT,
          url: model?.modelColor?.front?.url || model?.image || DEFAULT_IMAGE,
          dimension: model?.modelColor?.front?.dimension,
        },
      ];

      if (model?.modelColor?.rear?.url) {
        list.push({
          label: 'ด้านหลัง',
          view: IMAGE_VIEW.REAR,
          url: model?.modelColor?.rear?.url,
          dimension: model?.modelColor?.rear?.dimension,
        });
      }

      if (model?.interior?.url) {
        list.push({
          label: 'ภายใน',
          view: IMAGE_VIEW.INTERIOR,
          url: model?.interior?.url,
          dimension: model?.interior?.dimension,
        });
      }

      // force close modal when resize screen and image undefined
      if (modalCompare.open && !model?.image) {
        setModalCompare({ open: false, data: null });
      }
      return list;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      model?.image,
      model?.interior?.url,
      model?.modelColor?.front?.url,
      model?.modelColor?.rear?.url,
    ]);
    const onClickSlide = (
      swiper: any,
      event: MouseEvent | TouchEvent | PointerEvent
    ) => {
      if (images.length <= 1) return;
      onOpenModal(swiper?.realIndex);
      onClick && onClick(swiper, event);
    };

    const showBadgeEuro5 = useMemo(() => {
      if (!showBadge) return false;
      if (model?.modelCategory?.startsWith('เลือก')) return false;
      return !!model?.modelName || !!model?.modelCategory;
    }, [showBadge, model]);

    return (
      <SlideContainerStyled
        direction="column"
        justifyContent={images?.length > 1 && !showBadge ? 'between' : 'start'}
        disabled={!model || images?.length <= 1}
        css={css}
      >
        {images.length > 1 ? (
          <ModelSlider
            priority
            index={currentIndex}
            activeIndex={modalCompare.open ? undefined : activeSliderIndex}
            name={`${model?.name ?? 'compare'}`}
            bg="transparent"
            title={`compare_${currentIndex}`}
            images={images}
            ratio={ratio}
            rounded="full"
            onClick={onClickSlide}
            onSlideChange={onChange}
            pagination={{
              el: `.swiper-pagination-${name}-${currentIndex}`,
              clickable: true,
              renderBullet: function (index, className) {
                return `<div class="${className}" data-test="pagination_${
                  index + 1
                }"><span>${index + 1}</span></div>`;
              },
            }}
            css={{ cursor: images.length > 1 ? 'pointer' : undefined }}
          />
        ) : (
          <Image
            priority
            src={images[0].url}
            alt={images[0].label}
            width={600}
            height={ratio === '4:3' ? 450 : 338}
            layout="responsive"
            aspectRatio={ratio === '4:3' ? '4by3' : '16by9'}
            sources={[
              {
                srcset: images[0].url,
                media: { minWidth: 768 },
                options: {
                  width: 414,
                  crop: ratio,
                },
              },
              {
                srcset: images[0].url,
                media: { minWidth: 480, maxWidth: 767 },
                options: {
                  width: 320,
                  crop: ratio,
                },
              },
              {
                srcset: images[0].url,
                media: { maxWidth: 479 },
                options: {
                  width: 256,
                  crop: ratio,
                },
              },
            ]}
          />
        )}

        {!!showBadgeEuro5 && (
          <Box
            css={{
              paddingTop: '$space-4',
              '@md': {
                '& + *': {
                  paddingTop: '$space-8 !important',
                },
              },
            }}
            test={{ dataTest: `panel_badge_${currentIndex}` }}
          >
            <Badge>
              <IconUero5Max />
            </Badge>
          </Box>
        )}

        {images.length > 1 && (
          <PaginationBar
            css={{
              paddingTop: '$space-4',
            }}
            variant="red"
            justify="center"
          >
            <div
              className={`swiper-pagination swiper-pagination-${name}-${currentIndex}`}
              data-test={`panel_modelPaginations_${currentIndex}`}
            />
          </PaginationBar>
        )}

        {!!modalCompare.open && !!modalCompare?.data && (
          <Modal
            loop
            size="md"
            type="image"
            align="center"
            backgroundImage="white"
            autoPlay={{ delay: 4000 }}
            css={{ '.modal-body': { padding: 0 } }}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            items={images.map((image) => ({
              src: image?.url,
              alt: image?.label,
              width: image?.dimension?.width,
              height: image?.dimension?.height,
              aspectRatio: ratio === '4:3' ? '4by3' : '16by9',
            }))}
            activeItem={modalCompare.data?.index}
            open={modalCompare.open}
            onClose={onCloseModal}
            isShowCaption={false}
            isShowSocial={false}
          />
        )}
      </SlideContainerStyled>
    );
  }
);

CarSelectionSlider.displayName = 'CarSelectionSlider';
